import { useState, useMemo, useEffect } from 'react'
import { AdminAuthGuard } from '@/components/admin/AdminAuthGuard'
import { OnboardingListView } from '@/components/admin/OnboardingListView'
import { OnboardingDetailView } from '@/components/admin/OnboardingDetailView'
import { useOnboardings, useOnboardingDetail } from '@/hooks/useOnboardings'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogTitle, DialogTrigger } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Users, Clock, CheckCircle, AlertCircle, Plus, Search, Loader2, Check } from 'lucide-react'
import { collection, addDoc, Timestamp, doc, deleteDoc, setDoc } from 'firebase/firestore'
import { db } from '@/lib/firebase'
import { SCRAPE_INSTAGRAM_PROFILE_ENDPOINT, FINISH_ONBOARDING_ENDPOINT } from '@/config/endpoints'
import type { Artist, ArtistOnboarding } from '@/types/models'
import { useArtistsData } from '@/contexts/ArtistsDataContext'
import { cn } from '@/lib/utils'
import {
  fetchRecentArtistInvitesForAdmin,
  rankInviterSuggestionsFromInvites,
  type AdminFetchedArtistInvite,
  type InviterSuggestionFromInvite,
} from '@/lib/artistInvites'

/** Server runs embeddings, centroids, compression—often several minutes. */
const FINISH_ONBOARDING_TIMEOUT_MS = 15 * 60 * 1000

function invitingArtistLabel(artist: Artist): string {
  if (artist.firstName && artist.lastName) {
    return `${artist.firstName} ${artist.lastName.charAt(0)}.`
  }
  return artist.displayName
}

function artistMatchesInviterSearch(a: Artist, q: string): boolean {
  if (!q) return true
  return (
    (a.displayName?.toLowerCase().replace(/^@/, '') ?? '').includes(q) ||
    (a.firstName?.toLowerCase() ?? '').includes(q) ||
    (a.lastName?.toLowerCase() ?? '').includes(q) ||
    (a.location?.city?.toLowerCase() ?? '').includes(q)
  )
}

type InviterPickerRow =
  | { key: string; kind: 'artist'; artist: Artist; inviteSnippet?: string }
  | { key: string; kind: 'orphan'; artistId: string; inviteSnippet: string }

function buildInviterPickerRows(
  allArtists: Artist[],
  searchRaw: string,
  inviteSuggestions: InviterSuggestionFromInvite[],
): InviterPickerRow[] {
  const q = searchRaw.trim().toLowerCase()
  const seen = new Set<string>()
  const rows: InviterPickerRow[] = []

  for (const sug of inviteSuggestions) {
    if (seen.has(sug.artistId)) continue
    seen.add(sug.artistId)
    const artist = allArtists.find((a) => a.id === sug.artistId)
    if (artist) {
      rows.push({
        key: `invite-${sug.artistId}`,
        kind: 'artist',
        artist,
        inviteSnippet: sug.inviteInput,
      })
    } else {
      rows.push({
        key: `orphan-${sug.artistId}`,
        kind: 'orphan',
        artistId: sug.artistId,
        inviteSnippet: sug.inviteInput,
      })
    }
  }

  for (const artist of allArtists) {
    if (seen.has(artist.id)) continue
    if (!artistMatchesInviterSearch(artist, q)) continue
    rows.push({ key: artist.id, kind: 'artist', artist })
  }

  return rows
}

export default function ArtistOnboardingAdminPage() {
  const [selectedOnboardingId, setSelectedOnboardingId] = useState<string | null>(null)
  const [showNewOnboardingModal, setShowNewOnboardingModal] = useState(false)
  const [instagramUsername, setInstagramUsername] = useState('')
  const [invitingArtistId, setInvitingArtistId] = useState<string | null>(null)
  const [invitingArtistSearch, setInvitingArtistSearch] = useState('')
  const [isCreatingOnboarding, setIsCreatingOnboarding] = useState(false)
  const [recentInviteRows, setRecentInviteRows] = useState<AdminFetchedArtistInvite[]>([])
  const { allArtists, loading: artistsLoading } = useArtistsData()
  const { onboardings, loading: listLoading, error: listError, updateOnboarding: updateOnboardingInList, deleteOnboarding } = useOnboardings()
  const { onboarding: selectedOnboarding, loading: detailLoading, error: detailError, updateOnboarding: updateSelectedOnboarding } = useOnboardingDetail(selectedOnboardingId || undefined)

  useEffect(() => {
    if (!showNewOnboardingModal) {
      setRecentInviteRows([])
      return
    }

    let cancelled = false
    fetchRecentArtistInvitesForAdmin(450)
      .then((rows) => {
        if (!cancelled) setRecentInviteRows(rows)
      })
      .catch((err) => {
        console.error('[ArtistOnboardingAdminPage] Failed to load artistInvites:', err)
      })

    return () => {
      cancelled = true
    }
  }, [showNewOnboardingModal])

  const inviteInviterSuggestions = useMemo(
    () => rankInviterSuggestionsFromInvites(recentInviteRows, instagramUsername, 50),
    [recentInviteRows, instagramUsername],
  )

  const inviterPickerRows = useMemo(
    () => buildInviterPickerRows(allArtists, invitingArtistSearch, inviteInviterSuggestions),
    [allArtists, invitingArtistSearch, inviteInviterSuggestions],
  )

  const preferredInviterArtistIdFromInvites = inviteInviterSuggestions[0]?.artistId

  useEffect(() => {
    if (!showNewOnboardingModal) return
    if (!preferredInviterArtistIdFromInvites) return
    setInvitingArtistId(preferredInviterArtistIdFromInvites)
  }, [preferredInviterArtistIdFromInvites, showNewOnboardingModal])

  const handleViewDetails = (onboarding: ArtistOnboarding) => {
    setSelectedOnboardingId(onboarding.id)
  }

  const handleBackToList = () => {
    setSelectedOnboardingId(null)
  }

  const handleSave = async (updates: Partial<ArtistOnboarding>) => {
    if (!selectedOnboardingId) return
    
    try {
      await updateSelectedOnboarding(updates)
      // Also update in the list if needed
      await updateOnboardingInList(selectedOnboardingId, updates)
    } catch (error) {
      console.error('Error saving onboarding:', error)
      throw error
    }
  }

  const handleMarkReady = async (updates: Partial<ArtistOnboarding>) => {
    if (!selectedOnboardingId) return
    
    try {
      const readyUpdates = {
        ...updates,
        status: 'ready_for_artist' as const
      }
      
      await updateSelectedOnboarding(readyUpdates)
      // Also update in the list
      await updateOnboardingInList(selectedOnboardingId, readyUpdates)
    } catch (error) {
      console.error('Error marking onboarding as ready:', error)
      throw error
    }
  }

  const handlePublishArtist = async (updates: Partial<ArtistOnboarding>) => {
    if (!selectedOnboardingId) return
    
    try {
      // First update the arrays in Firestore
      await updateSelectedOnboarding(updates)
      await updateOnboardingInList(selectedOnboardingId, updates)
      
      const controller = new AbortController()
      const timeoutId = window.setTimeout(() => controller.abort(), FINISH_ONBOARDING_TIMEOUT_MS)

      let response: Response
      try {
        response = await fetch(FINISH_ONBOARDING_ENDPOINT, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            onboardingId: selectedOnboardingId
          }),
          signal: controller.signal,
        })
      } finally {
        window.clearTimeout(timeoutId)
      }

      const text = await response.text()
      let message = text
      try {
        const json = JSON.parse(text) as { detail?: string; error?: string; message?: string }
        if (!response.ok) {
          message =
            (typeof json.detail === 'string' && json.detail) ||
            (typeof json.error === 'string' && json.error) ||
            (typeof json.message === 'string' && json.message) ||
            text ||
            response.statusText
        }
      } catch {
        if (!response.ok) {
          message = text || `${response.status} ${response.statusText}`
        }
      }

      if (!response.ok) {
        throw new Error(`Failed to finish onboarding: ${response.status} — ${message}`)
      }

      console.log('Artist published successfully', message.slice(0, 500))
    } catch (error) {
      const isAbort =
        (error instanceof DOMException || error instanceof Error) && error.name === 'AbortError'
      if (isAbort) {
        throw new Error(
          `Publishing timed out after ${FINISH_ONBOARDING_TIMEOUT_MS / 60000} minutes. The server may still be processing—check Firestore for the artist and onboarding status before trying again.`
        )
      }
      console.error('Error publishing artist:', error)
      throw error
    }
  }

  // Helper function to recursively remove undefined values (Firestore doesn't allow them)
  const removeUndefinedValues = (obj: any): any => {
    if (obj === null || obj === undefined) {
      return null
    }
    
    if (Array.isArray(obj)) {
      return obj.map(item => removeUndefinedValues(item))
    }
    
    if (typeof obj === 'object') {
      const cleaned: any = {}
      for (const [key, value] of Object.entries(obj)) {
        if (value !== undefined) {
          cleaned[key] = removeUndefinedValues(value)
        }
      }
      return cleaned
    }
    
    return obj
  }

  const handleArchiveOnboarding = async (onboarding: ArtistOnboarding) => {
    try {
      // Copy the document to the onboardingArchived collection
      const archivedData = {
        ...onboarding,
        archivedAt: Timestamp.now(),
        originalId: onboarding.id
      }
      
      // Clean undefined values before sending to Firestore
      const cleanedArchivedData = removeUndefinedValues(archivedData)
      
      // Add to archived collection
      await setDoc(doc(db, 'onboardingArchived', onboarding.id), cleanedArchivedData)
      
      // Delete from original collection
      await deleteDoc(doc(db, 'onboardings', onboarding.id))
      
      // If we're currently viewing the deleted onboarding, go back to list
      if (selectedOnboardingId === onboarding.id) {
        setSelectedOnboardingId(null)
      }
      
      // Update local state
      if (deleteOnboarding) {
        deleteOnboarding(onboarding.id)
      }
      
      console.log('Onboarding archived successfully:', onboarding.displayName)
    } catch (error) {
      console.error('Error archiving onboarding:', error)
      throw error
    }
  }

  const handleStartNewOnboarding = async () => {
    if (!instagramUsername.trim()) return
    
    setIsCreatingOnboarding(true)
    
    try {
      // Create new onboarding document in Firestore
      const newOnboardingData = {
        bio: '',
        displayName: instagramUsername.trim(),
        postUrls: [],
        profileImageUrl: '',
        status: 'scraper_started' as const,
        location: '',
        tattooImageUrls: [],
        flashes: [],
        agreementAccepted: false,
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
        ...(invitingArtistId ? { invitingArtist: invitingArtistId } : {}),
      }
      
      const docRef = await addDoc(collection(db, 'onboardings'), newOnboardingData)
      
      // Close modal and reset form immediately after creating the document
      setShowNewOnboardingModal(false)
      setInstagramUsername('')
      setInvitingArtistId(null)
      setIsCreatingOnboarding(false)
      
      // Call the scraper API in the background (fire-and-forget)
      fetch(SCRAPE_INSTAGRAM_PROFILE_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          artistId: docRef.id,
          username: instagramUsername.trim(),
          days: 7,
          dryRun: false,
          postsOnly: false,
        }),
      }).catch(error => {
        console.error('Background API call failed:', error)
        // Optionally, you could update the document status to indicate failure
        // But we don't want to show user-facing errors since this is fire-and-forget
      })
      
    } catch (error) {
      console.error('Error creating new onboarding:', error)
      alert('Failed to start new onboarding. Please try again.')
      setIsCreatingOnboarding(false)
    }
  }

  const onboardingsFilteredForList = onboardings.filter(o => o.status !== 'done')

  // Calculate statistics
  const stats = {
    total: onboardings.length,
    scraperStarted: onboardingsFilteredForList.filter(o => o.status === 'scraper_started').length,
    scraperDone: onboardingsFilteredForList.filter(o => o.status === 'scraper_done').length,
    readyForArtist: onboardingsFilteredForList.filter(o => o.status === 'ready_for_artist').length,
    waitingForReview: onboardingsFilteredForList.filter(o => o.status === 'waiting_for_review').length,
    completed: onboardings.filter(o => o.status === 'done').length
  }

  return (
    <AdminAuthGuard>
      <div className="min-h-screen bg-background">
        <div className="container mx-auto px-4 py-8">
          {/* Header */}
          <div className="mb-8">
            <div className="flex justify-between items-start">
              <div>
                <h1 className="text-3xl font-bold mb-2">Artist Onboarding Admin</h1>
                <p className="text-muted-foreground">
                  Manage and review artist onboarding applications
                </p>
              </div>
              
              <Dialog
                open={showNewOnboardingModal}
                onOpenChange={(open) => {
                  setShowNewOnboardingModal(open)
                  if (!open) {
                    setInvitingArtistId(null)
                    setInvitingArtistSearch('')
                  }
                }}
              >
                <DialogTrigger asChild>
                  <Button>
                    <Plus className="h-4 w-4 mr-2" />
                    Start New Onboarding
                  </Button>
                </DialogTrigger>
                <DialogContent
                  className={cn(
                    'gap-0 p-0 sm:max-w-md sm:rounded-[var(--card-radius)] max-h-[min(94dvh,880px)] min-h-[min(70dvh,520px)] flex flex-col overflow-hidden shadow-lg border-border',
                  )}
                >
                  <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-x-hidden overflow-y-auto p-6">
                    <div className="space-y-1.5 pr-8">
                      <DialogTitle className="text-xl font-semibold tracking-tight text-foreground leading-tight">
                        Start new onboarding
                      </DialogTitle>
                      <DialogDescription className="text-sm leading-relaxed text-muted-foreground">
                        Enter the Instagram handle to scrape. If an in-app invite matches that handle, the inviting artist moves to the top of the list (and is pre-selected).
                      </DialogDescription>
                    </div>

                    <div className="space-y-2">
                      <label htmlFor="instagram-username" className="text-sm font-medium text-foreground">
                        Instagram username
                      </label>
                      <Input
                        id="instagram-username"
                        placeholder="@username"
                        value={instagramUsername}
                        onChange={(e) => setInstagramUsername(e.target.value)}
                        disabled={isCreatingOnboarding}
                        enterKeyHint="done"
                        className={cn(
                          'h-12 rounded-xl text-base transition-[opacity,background-color,border-color,color]',
                          'border-border bg-background',
                          isCreatingOnboarding && 'opacity-50 pointer-events-none',
                        )}
                      />
                    </div>

                    <div className="flex min-h-0 flex-1 flex-col gap-3 pt-1">
                      <p className="text-sm font-medium text-foreground">Inviting artist (optional)</p>
                      <p className="text-sm leading-relaxed text-muted-foreground">
                        Matches from <span className="font-mono text-xs">artistInvites</span> appear at the top. Saved as{' '}
                        <span className="font-mono text-xs">invitingArtist</span>.
                      </p>
                      <div className="relative">
                        <Search
                          className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
                          aria-hidden
                        />
                        <Input
                          value={invitingArtistSearch}
                          onChange={(e) => setInvitingArtistSearch(e.target.value)}
                          placeholder="Search artists…"
                          disabled={isCreatingOnboarding}
                          className={cn(
                            'h-12 rounded-xl pl-9 text-base',
                            isCreatingOnboarding && 'opacity-50',
                          )}
                        />
                      </div>

                      <div className="min-h-[260px] flex-1 overflow-y-auto rounded-xl border border-border divide-y divide-border bg-background">
                        {artistsLoading ? (
                          <div className="flex items-center justify-center gap-2 py-12 text-sm text-muted-foreground">
                            <Loader2 className="h-4 w-4 animate-spin shrink-0" aria-hidden />
                            Loading artists…
                          </div>
                        ) : inviterPickerRows.length === 0 ? (
                          <p className="py-10 px-3 text-center text-sm text-muted-foreground">
                            No artists match your search.
                          </p>
                        ) : (
                          inviterPickerRows.map((row) => {
                            if (row.kind === 'orphan') {
                              const selected = invitingArtistId === row.artistId
                              const avatarSrc = `https://ui-avatars.com/api/?name=${encodeURIComponent(row.artistId.slice(0, 6))}`
                              return (
                                <button
                                  key={row.key}
                                  type="button"
                                  disabled={isCreatingOnboarding}
                                  onClick={() =>
                                    setInvitingArtistId((prev) =>
                                      prev === row.artistId ? null : row.artistId,
                                    )
                                  }
                                  className={cn(
                                    'flex w-full items-center gap-3 px-3 py-2.5 text-left transition-colors hover:bg-muted/80',
                                    selected && 'bg-primary/10',
                                    isCreatingOnboarding && 'pointer-events-none opacity-50',
                                  )}
                                >
                                  <img
                                    src={avatarSrc}
                                    alt=""
                                    className="h-10 w-10 shrink-0 rounded-full bg-muted object-cover"
                                  />
                                  <div className="min-w-0 flex-1">
                                    <p className="truncate font-medium text-foreground">
                                      Inviting artist{' '}
                                      <span className="font-mono text-xs">{row.artistId.slice(0, 10)}…</span>
                                    </p>
                                    <p className="line-clamp-2 text-xs text-muted-foreground">
                                      Invite: “{row.inviteSnippet}”
                                    </p>
                                  </div>
                                  {selected ? (
                                    <Check className="h-5 w-5 shrink-0 text-primary" aria-hidden strokeWidth={2.5} />
                                  ) : null}
                                </button>
                              )
                            }

                            const { artist } = row
                            const selected = invitingArtistId === artist.id
                            const avatarSrc =
                              artist.compressedAvatarUrl ||
                              artist.avatarUrl ||
                              `https://ui-avatars.com/api/?name=${encodeURIComponent(invitingArtistLabel(artist))}`
                            return (
                              <button
                                key={row.key}
                                type="button"
                                disabled={isCreatingOnboarding}
                                onClick={() => {
                                  setInvitingArtistId((prev) => (prev === artist.id ? null : artist.id))
                                }}
                                className={cn(
                                  'flex w-full items-center gap-3 px-3 py-2.5 text-left transition-colors hover:bg-muted/80',
                                  selected && 'bg-primary/10',
                                  isCreatingOnboarding && 'pointer-events-none opacity-50',
                                  row.inviteSnippet && !selected && 'bg-muted/25',
                                )}
                              >
                                <img
                                  src={avatarSrc}
                                  alt=""
                                  className="h-10 w-10 shrink-0 rounded-full bg-muted object-cover"
                                />
                                <div className="min-w-0 flex-1">
                                  <p className="truncate font-medium text-foreground">
                                    {invitingArtistLabel(artist)}
                                  </p>
                                  <p className="line-clamp-2 text-xs text-muted-foreground">
                                    {row.inviteSnippet ? (
                                      <>Invite: “{row.inviteSnippet}”</>
                                    ) : (
                                      <>
                                        @{artist.displayName.replace(/^@/, '')}
                                        {artist.location?.city ? ` · ${artist.location.city}` : ''}
                                      </>
                                    )}
                                  </p>
                                </div>
                                {selected ? (
                                  <Check className="h-5 w-5 shrink-0 text-primary" aria-hidden strokeWidth={2.5} />
                                ) : null}
                              </button>
                            )
                          })
                        )}
                      </div>

                      {invitingArtistId ? (
                        <button
                          type="button"
                          className="text-sm text-muted-foreground underline-offset-4 hover:text-foreground hover:underline disabled:opacity-50"
                          disabled={isCreatingOnboarding}
                          onClick={() => setInvitingArtistId(null)}
                        >
                          Clear inviting artist
                        </button>
                      ) : null}
                    </div>
                  </div>

                  <div className="flex flex-col gap-2 border-t border-border p-6 pt-4 shrink-0 bg-background">
                    <Button
                      type="button"
                      className="h-12 w-full gap-2 rounded-xl text-base font-medium transition-colors duration-300"
                      disabled={!instagramUsername.trim() || isCreatingOnboarding}
                      onClick={() => void handleStartNewOnboarding()}
                    >
                      {isCreatingOnboarding ? (
                        <>
                          <Loader2 className="h-5 w-5 shrink-0 animate-spin" aria-hidden />
                          Starting…
                        </>
                      ) : (
                        "Let's go"
                      )}
                    </Button>
                    <Button
                      type="button"
                      variant="secondary"
                      className="h-12 w-full rounded-xl text-base font-medium"
                      disabled={isCreatingOnboarding}
                      onClick={() => setShowNewOnboardingModal(false)}
                    >
                      Cancel
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>
            </div>
          </div>

          {selectedOnboardingId && selectedOnboarding ? (
            // Detail View
            <OnboardingDetailView
              onboarding={selectedOnboarding}
              loading={detailLoading}
              onBack={handleBackToList}
              onSave={handleSave}
              onMarkReady={handleMarkReady}
              onPublishArtist={handlePublishArtist}
              artistsForLookup={allArtists}
            />
          ) : (
            // List View
            <div className="space-y-6">
              {/* Statistics Cards */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-4">
                <Card>
                  <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                    <CardTitle className="text-sm font-medium">Total Onboardings</CardTitle>
                    <Users className="h-4 w-4 text-muted-foreground" />
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">{stats.total}</div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                    <CardTitle className="text-sm font-medium">Scraper Started</CardTitle>
                    <Clock className="h-4 w-4 text-orange-600" />
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold text-orange-600">{stats.scraperStarted}</div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                    <CardTitle className="text-sm font-medium">Scraper Done</CardTitle>
                    <Clock className="h-4 w-4 text-yellow-600" />
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold text-yellow-600">{stats.scraperDone}</div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                    <CardTitle className="text-sm font-medium">Ready for Artist</CardTitle>
                    <AlertCircle className="h-4 w-4 text-blue-600" />
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold text-blue-600">{stats.readyForArtist}</div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                    <CardTitle className="text-sm font-medium">Waiting for Review</CardTitle>
                    <Clock className="h-4 w-4 text-purple-600" />
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold text-purple-600">{stats.waitingForReview}</div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                    <CardTitle className="text-sm font-medium">Completed</CardTitle>
                    <CheckCircle className="h-4 w-4 text-green-600" />
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold text-green-600">{stats.completed}</div>
                  </CardContent>
                </Card>
              </div>

              {/* Error Display */}
              {(listError || detailError) && (
                <Card className="border-destructive">
                  <CardHeader>
                    <CardTitle className="text-destructive">Error</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <p className="text-destructive">
                      {listError?.message || detailError?.message || 'An error occurred'}
                    </p>
                    {(listError?.message?.includes('permission') || detailError?.message?.includes('permission')) && (
                      <div className="space-y-2">
                        <p className="text-sm text-muted-foreground">
                          If you're experiencing permission issues, try:
                        </p>
                        <ul className="text-sm text-muted-foreground list-disc list-inside space-y-1">
                          <li>Refreshing the page</li>
                          <li>Signing out and back in</li>
                          <li>Clearing your browser cache</li>
                        </ul>
                        <Button 
                          onClick={() => window.location.reload()} 
                          variant="outline" 
                          size="sm"
                          className="mt-2"
                        >
                          Refresh Page
                        </Button>
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}

              {/* Onboarding List */}
              <Card>
                <CardHeader>
                  <CardTitle>Artist Onboardings</CardTitle>
                  <CardDescription>
                    Click on any onboarding to view and edit details
                  </CardDescription>
                </CardHeader>
                <CardContent>
              <OnboardingListView
                onboardings={onboardingsFilteredForList}
                loading={listLoading}
                onViewDetails={handleViewDetails}
                onDelete={handleArchiveOnboarding}
                artistsForLookup={allArtists}
              />
                </CardContent>
              </Card>
            </div>
          )}
        </div>
      </div>
    </AdminAuthGuard>
  )
}
